import { toast } from "sonner";
import { aiColumnsApi } from "@/api/deals-api";
import { getErrorMessage } from "@/lib/api-error";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import { useDealsListStore } from "@/store/deals-list-store";
import type { AiCellValue } from "./ai-column-cell";

/**
 * Run one AI column for one deal, from whichever table the cell is in.
 *
 * The request takes seconds to come back, and the cell is what was clicked —
 * it has to answer for itself immediately or the click reads as ignored. The
 * server sets the same "running" state; this just gets there first, and a
 * failure puts the previous answer back.
 */
export function useRunAiCell({
  deals,
  workspaceId,
  onAiColumnRun,
}: {
  deals: DealTransformed[];
  workspaceId: string;
  onAiColumnRun?: () => void;
}) {
  const updateDeal = useDealsListStore((state) => state.updateDeal);

  const markCell = (
    dealId: string,
    columnId: string,
    cell: AiCellValue | undefined
  ) => {
    const current = deals.find((d) => d.id === dealId)?.aiColumnValues ?? {};
    updateDeal(dealId, {
      aiColumnValues: { ...current, [columnId]: cell },
    });
  };

  return async (deal: DealTransformed, columnId: string) => {
    const previous = (deal.aiColumnValues ?? {})[columnId] as
      | AiCellValue
      | undefined;
    markCell(deal.id, columnId, {
      // Carry the old answer into the running state so the cell shows it while
      // the new one is worked out.
      ...(previous ?? {}),
      status: "running",
      run_at: new Date().toISOString(),
      error: undefined,
    });
    try {
      await aiColumnsApi.run(workspaceId, columnId, [deal.id]);
      onAiColumnRun?.();
    } catch (error) {
      markCell(deal.id, columnId, previous);
      toast.error(getErrorMessage(error, "Couldn't start that run"));
    }
  };
}
